import clsx from 'clsx';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditableText } from '@/core/EditableText.atom';
import { FormError } from '@/core/FormError.atom';
import { DateTimeEntry } from '@/datetime/DateTimeEntry.atom';
import { STRING_UOM } from '@/main/app.constants';
import { triggerManualSignalPreview } from '@/tools/manualSignal/manualSignal.actions';
import { SAMPLE_VALUE_ERROR } from '@/tools/manualSignal/manualSignal.constants';
import {
  useManualSignalActions,
  useManualSignalSamples,
  useManualSignalUom,
} from '@/tools/manualSignal/manualSignal.store';
import { base64guid } from '@/utilities/guid.utilities';
import { Button } from '@seeqdev/qomponents';

interface SampleInputProps {
  testId: string;
  sample?: { dateTime: any; value: any; id?: string };
  timezone: {
    name: string;
    displayName: string;
    offset: string;
    offsetMinutes: number;
  };
}

export const SampleInput = ({ sample, timezone, testId }: SampleInputProps) => {
  const { t } = useTranslation();

  const samples = useManualSignalSamples();
  const isStringUom = useManualSignalUom() === STRING_UOM;
  const isStringSignal = isStringUom || Number.isNaN(Number(samples[0]?.value));
  const { addSample, updateSample, setEditingSampleId } = useManualSignalActions();
  const [dateTime, setDateTime] = useState<moment.Moment>(
    sample?.dateTime ? moment.utc(sample.dateTime) : moment.utc().startOf('minute'),
  );
  const [value, setValue] = useState(sample?.value ?? '');
  const [valueError, setValueError] = useState<string | undefined>(undefined);
  const [dateError, setDateError] = useState(false);

  useEffect(() => {
    const newDateTimeValue = dateTime.valueOf();
    const sampleId = sample?.id;
    const hasError = _.some(samples, (s) => s.dateTime === newDateTimeValue && s.id !== sampleId);
    setDateError(hasError && !_.isEmpty(value));
  }, [samples, dateTime, value]);

  useEffect(() => {
    const hasValueTypeError = !isStringSignal && _.isNaN(_.toNumber(value));
    setValueError(hasValueTypeError ? SAMPLE_VALUE_ERROR.INVALID_TYPE : undefined);
  }, [value, isStringSignal, samples, isStringUom]);

  const resetState = () => {
    setValue('');
    setDateTime(moment.utc().startOf('minute'));
    setValueError(undefined);
    setDateError(false);
  };

  const addNewSample = () => {
    if (_.isEmpty(value)) {
      setValueError(SAMPLE_VALUE_ERROR.EMPTY);
      return;
    }
    if (dateError || !_.isUndefined(valueError)) return;

    addSample({ id: base64guid(), dateTime: dateTime.valueOf(), value });
    triggerManualSignalPreview();
    resetState();
  };

  const saveEditedSample = () => {
    if (dateError || !_.isUndefined(valueError) || !sample) return;

    updateSample({ id: sample.id, dateTime: dateTime.valueOf(), value });
    setEditingSampleId(undefined);
    triggerManualSignalPreview();
    resetState();
  };

  const handleDateTimeUpdate = (date: moment.Moment | undefined) => {
    if (date) {
      setDateTime(date);
    }
  };

  return (
    <div className="sampleInput flexRowContainer tw:pt-sq-10 tw:pb-sq-10" data-testid={testId}>
      <div className="flexColumnContainer flexFill text-center tw:pr-sq-10 tw:pl-sq-10 tw:font-bold">
        <div className="flexFillEqually">{t('MANUAL_SIGNAL.DATE_TIME')}</div>
        <div className="flexFillEqually">{t('MANUAL_SIGNAL.VALUE')}</div>
      </div>
      <div className="flexColumnContainer flexFill sq-text-primary text-center tw:pr-sq-10 tw:pl-sq-10">
        <div className="flexFillEqually tw:self-center">
          <DateTimeEntry
            testId="manualSignalDateTimeEntry"
            hideTimezone={true}
            trackCategory="Workbench_Tool"
            trackAction="Manual_Signal"
            trackInformation="Date time changed"
            datePickerExtraClassNames={clsx({ 'sq-text-danger': dateError })}
            updateDate={handleDateTimeUpdate}
            timezone={timezone}
            date={dateTime}
            showSeconds={false}
          />
        </div>
        <div className="flexFillEqually tw:self-center">
          <div className="nowrap">
            <EditableText
              value={_.isEmpty(value) ? t('MANUAL_SIGNAL.VALUE_PLACEHOLDER') : value}
              inputClasses="text-center"
              testId={sample?.id ? `sampleValue-${sample.id}` : 'newSampleValue'}
              maxDisplayChars={15}
              onUpdate={(newValue) => {
                setValueError(_.isEmpty(newValue) ? SAMPLE_VALUE_ERROR.EMPTY : undefined);
                setValue(newValue);
              }}
              textClasses={clsx(
                'tw:pt-sq-1 tw:pb-sq-1',
                { 'tw:italic': _.isEmpty(value) },
                { 'sq-text-danger': !_.isUndefined(valueError) },
              )}
            />
          </div>
        </div>
      </div>

      {sample?.id ? (
        <div className="flexColumnContainer tw:mt-sq-5 tw:justify-center">
          <Button
            label={t('CANCEL')}
            extraClassNames="inlineBlock mr10"
            onClick={() => {
              setEditingSampleId(undefined);
              triggerManualSignalPreview();
            }}
          />
          <Button
            testId="saveSampleButton"
            label={t('SAVE')}
            variant="theme"
            disabled={_.isEmpty(value)}
            extraClassNames="inlineBlock"
            onClick={saveEditedSample}
          />
        </div>
      ) : (
        <div className="text-center tw:mt-sq-5">
          <Button
            testId="addSampleButton"
            label={t('MANUAL_SIGNAL.ADD_SAMPLE')}
            extraClassNames="inlineBlock"
            onClick={addNewSample}
          />
        </div>
      )}

      {dateError && <FormError extraClassNames="mt5 ml10 mr1 mb0" errorText="MANUAL_SIGNAL.DATE_TIME_ERROR" />}

      {valueError === SAMPLE_VALUE_ERROR.INVALID_TYPE && (
        <FormError extraClassNames="mt5 ml10 mr1 mb0" errorText="MANUAL_SIGNAL.VALUE_TYPE_ERROR" />
      )}
    </div>
  );
};
